import "./PersonalData.scss";

import {
  Box,
  Card,
  Grid,
  Typography,
} from "@material-ui/core";
import { ActivityType, InvestmentClass } from "common/backend/types/utils";
import { CommonToast } from "common/components";
import { SubmitButton } from "common/components/input/elements";
import { UserField, UserInputTypeKey } from "common/components/input/fields";
import { UpdateUser } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { StoreState } from "common/store/types";
import {
  UpdateUserManageable,
  UserManageable,
} from "common/store/user/types";
import { isEmptyObject } from "common/utils/utility";
import { errorMessages } from "common/utils/validation";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import { Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ActivityDetails, SkillDetails } from "shared/subforms/UserExperienceSubform";

export const PersonalData = () => {
  // State
  const [toastOpen, setToastOpen] = React.useState(false);

  // Hooks
  const { t } = useTranslation();

  let {
    execute: updateUser, loading
  } = useCustomQuery(UpdateUser({
    onSuccess: () => {
      setToastOpen(true);
    },
  }));

  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);

  const frontendOnlyFields = [
    "password",
    "walletPassword",
    "confirmWalletPassword",
  ]

  // Validation
  const onValidate = (values: UserManageable) => {
    const updatableValues = new UpdateUserManageable(values);

    const errors: any = updatableValues.validate(
      Object.keys(updatableValues).filter((key: string) => !frontendOnlyFields.includes(key)),
      errorMessages
    );

    return errors;
  };

  const onSubmit = async (
    values: UserManageable, { setSubmitting }: any
  ) => {
    setSubmitting(true);
    const input = new UpdateUserManageable(values).serialize(
      Object.keys(values),
      storageUserDetails
    );
    if (!isEmptyObject(input)) {
      await updateUser({ variables: { input } });
    }
    setSubmitting(false);
  };

  // Helpers
  const getClassName = (id: string) => {
    switch (id) {
      case "addressCountry":
      case "citizenship":
      case "idDocumentCountry":
      case "highestEducation":
        return "autocomplete";
      default:
        return "";
    }
  };

  // Options
  const initialUserValues = new UserManageable(storageUserDetails);

  const excludedFields = frontendOnlyFields.concat([
    "notUSTax",
    "experience",
    "notProvidingExperience",
  ]);
  const displayedUserFields = Object.keys(new UpdateUserManageable(initialUserValues)).filter((field) => !excludedFields.includes(field));

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        <Box className="personal-data">
          <Grid item className="section">
            <Typography className="section-title" variant="subtitle1">
              {t("Personal information")}
            </Typography>

            <Card className={"card"}>
              <Formik
                validate={onValidate}
                onSubmit={onSubmit}
                initialValues={initialUserValues}
              >
                {(props) => {
                  const { isSubmitting } = props;

                  return (
                    <Form autoComplete="off">
                      <Grid
                        container
                        direction="row"
                        spacing={4}
                        className="fields-container"
                      >
                        {displayedUserFields.map((
                          field, i
                        ) =>
                          <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                            className="field-wrapper"
                            key={i}
                          >
                            <UserField
                              id={field as UserInputTypeKey}
                              className={getClassName(field)}
                              {...props}
                            />
                          </Grid>)}
                      </Grid>

                      <Grid container spacing={5} direction="row">
                        {Object.values(InvestmentClass).map((
                          value: any, index: number
                        ) =>
                          <Grid xs={12} md={6} item key={index}>
                            <SkillDetails indexkey={value} {...props} disabled={true} />
                          </Grid>)}

                        {Object.values(ActivityType).map((
                          value: any, index: number
                        ) =>
                          <Grid xs={12} md={6} item key={index}>
                            <ActivityDetails indexkey={value} {...props} disabled={true} />
                          </Grid>)}
                      </Grid>

                      <Grid
                        container
                        item
                        direction="row"
                        className="button-container"
                      >
                        <SubmitButton
                          value={t("Update")}
                          {...props}
                          isSubmitting={loading || isSubmitting}
                        />
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </Card>
          </Grid>

          <CommonToast
            open={toastOpen}
            onClose={() => setToastOpen(false)}
            text={t("Changes saved!")}
            severity={"success"}
          />
        </Box>
      </ContentWrapper>
    </Box>
  );
};
